<template>
  <div class="space-y-4">
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-semibold mr-auto">Activity</h1>
      <div class="flex gap-2">
        <Dropdown
          v-model="selectedUser"
          :options="userOptions"
          optionLabel="name"
          optionValue="id"
          placeholder="Filter by user"
          class="w-48"
        />
        <MultiSelect
          v-model="selectedTypes"
          :options="typeOptions"
          optionLabel="name"
          optionValue="value"
          placeholder="Filter by type"
          class="w-48"
        />
        <Button icon="pi pi-filter-slash" @click="resetFilters" />
      </div>
    </div>

    <div class="activity-timeline bg-white rounded-lg p-4 shadow">
      <DataTable
        v-if="!loading && events.length"
        :value="filteredEvents"
        stripedRows
        sortField="date"
        :sortOrder="-1"
      >
        <Column field="type" header="Type">
          <template #body="{ data }">
            <div class="flex items-center">
              <i :class="getIconForType(data.type)" class="mr-2"></i>
              <span class="capitalize">{{ data.type }}</span>
            </div>
          </template>
        </Column>
        <Column field="title" header="Title" />
        <Column field="organizationId" header="Organization">
          <template #body="{ data }">
            <template
              v-if="typeof data.organizationId === 'string' && data.organizationId.trim() !== ''"
            >
              <router-link
                v-if="organizationNames[data.organizationId]"
                :to="`/crm/organizations/${data.organizationId}`"
                class="text-blue-600 hover:text-blue-800 hover:underline"
              >
                {{ organizationNames[data.organizationId] }}
              </router-link>
              <span v-else class="text-gray-400 italic">Loading...</span>
            </template>
            <span v-else class="text-gray-400">—</span>
          </template>
        </Column>
        <Column field="description" header="Description">
          <template #body="{ data }">
            <div class="max-w-md truncate">{{ data.description }}</div>
          </template>
        </Column>
        <Column field="date" header="Date" sortable>
          <template #body="{ data }">
            {{ formatDate(data.date) }}
          </template>
        </Column>
        <Column header="Actions">
          <template #body="{ data }">
            <Button icon="pi pi-eye" class="p-button-text p-button-sm" @click="viewDetails(data)" />
          </template>
        </Column>
      </DataTable>

      <div v-if="loading" class="flex justify-center py-8">
        <ProgressSpinner />
      </div>

      <div v-else-if="!events.length" class="py-8 text-center text-gray-500">
        No activities found.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { getGlobalTimeline } from '@/services/crm/crmTimelineService'
import type { TimelineEvent } from '@/components/crm/CrmTimeline.vue'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import MultiSelect from 'primevue/multiselect'
import ProgressSpinner from 'primevue/progressspinner'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { useOrganizationStore } from '@/stores/v2/organization'

// Services
const organizationStore = useOrganizationStore()

// State
const events = ref<TimelineEvent[]>([])
const loading = ref(true)
const selectedUser = ref<string | null>(null)
const selectedTypes = ref<string[]>([])
const organizationNames = ref<Record<string, string>>({})

// Options for filters
const userOptions = [
  { name: 'All Users', id: null },
  { name: 'John Doe', id: 'user1' },
  { name: 'Jane Smith', id: 'user2' },
  // This would be populated from your users collection
]

const typeOptions = [
  { name: 'Calls', value: 'call' },
  { name: 'Emails', value: 'email' },
  { name: 'Comments', value: 'comment' },
  { name: 'Tasks', value: 'task' },
  { name: 'Transactions', value: 'transaction' },
]

// Computed
const filteredEvents = computed(() => {
  let result = [...events.value]

  // Filter by type if types are selected
  if (selectedTypes.value.length > 0) {
    result = result.filter((event) => selectedTypes.value.includes(event.type))
  }

  return result
})

// Methods
async function loadTimeline() {
  loading.value = true
  try {
    events.value = await getGlobalTimeline(selectedUser.value || undefined)
    // Debug the events structure
    console.log('Timeline events:', events.value)
    console.log('Sample event structure:', events.value.length > 0 ? events.value[0] : 'No events')
    // Load organization names
    await loadOrganizationNames()
  } catch (error) {
    console.error('Error loading timeline:', error)
  } finally {
    loading.value = false
  }
}

async function loadOrganizationNames() {
  // Extract unique organization IDs that are valid strings (not null/undefined)
  const organizationIds = events.value
    .filter(
      (event) => typeof event.organizationId === 'string' && event.organizationId.trim() !== '',
    )
    .map((event) => event.organizationId as string)
    .filter((value, index, self) => self.indexOf(value) === index)

  // Get organization names map
  const search = await organizationStore.search()
  const organizations = await search.recentlyUpdated(1000).run()
  const nameMap: Record<string, string> = {}

  organizations.forEach((org) => {
    if (org.id && organizationIds.includes(org.id)) {
      nameMap[org.id] = org.name
    }
  })

  organizationNames.value = nameMap
}

function resetFilters() {
  selectedUser.value = null
  selectedTypes.value = []
  loadTimeline()
}

function formatDate(date: Date | string) {
  if (!date) return ''
  const d = new Date(date)
  return (
    d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  )
}

function getIconForType(type: string): string {
  const iconMap: Record<string, string> = {
    call: 'pi pi-phone',
    email: 'pi pi-envelope',
    comment: 'pi pi-comment',
    task: 'pi pi-check-square',
    transaction: 'pi pi-dollar',
  }
  return iconMap[type] || 'pi pi-circle'
}

function viewDetails(activity: TimelineEvent) {
  console.log('View details for:', activity)
  // Implement view details functionality - could open a modal or navigate to detail page
}

// Lifecycle
onMounted(async () => {
  await loadTimeline()
})

// Watch for user changes
watch(selectedUser, async () => {
  await loadTimeline()
})
</script>
